import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { notification } from 'ui';
import moment from 'utils/cops-moment';
import useDoNotShow from '../../../../../utils/hooks/useDoNotShow';
import { blockConfig, ItemTypes, laneConfig, viewConfig } from '../../../config';
import { getTopDimension, getTotalLines, isAfterHours } from '../../../engine/block-dimensions';
import getDayHourOfDroppedBlock from '../../../engine/dropped-engine';
import mapBlock from '../../../utils/map-block';
import { getWarningDescription } from '../../block/components/block-detail-actions';
import BlockAfterHours from '../../block/containers/block-after-hours-container';
import BlockWrapper from '../../block/containers/block-wrapper-container';

const DropZoneWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
`;

const ConfirmButton = styled(Button)`
  color: white;
  background-color: #6f7e95;
  &:hover {
    opacity: 0.7;
    color: white;
    background-color: #6f7e95;
  }
`;

/**
 *  Lane dropzone component
 *
 * @param {id} string - id of the lane
 * @param {blocks} object - array of data of block
 */
const LaneDropzone = ({
  id,
  blocks = [],
  setTotalLines,
  viewDimensions,
  ganttDimensions,
  setIsDraggingOver,
  dateReference,
  intl,
  addBlockData,
  updateBlockData,
  setLaneInfoIsDraggingOver,
  selectedIds,
  renderMenu,
  view,
  isLoadingBoardData,
  setLaneInfo,
  addMultiBlockData,
  updateMultiPlanningBlockData,
  multiStatusInfo,
  setBlockPreview,
  setBlockPreviewFailure,
}) => {
  let prevPrioAfterHours = 0;
  let prevLeftAfterHours = 0;
  const [mappedBlocks, setMappedBlocks] = useState([]);

  const [doNotShowReschedule, checkbox] = useDoNotShow('confirmedToTentative');
  const { adjacentBlock, boardData } = useSelector(state => state.gantt);
  const { showTravelTime } = useSelector(state => state.planningBoard);

  useEffect(() => {
    const arrayLinePositions = [];
    setMappedBlocks(
      blocks
        ? blocks
          ?.map(block => mapBlock(block, boardData?.calculateTravelTime ? true : showTravelTime))
          // filter out block with duration === 0
          ?.filter(block =>
            block.durationInMinutes !== 0 &&
            !block.isDeleted &&
            block.showAs !== 'hidden'
          )
          // sort on start hour
          ?.sort((blockA, blockB) => blockA.startMoment.unix() - blockB.startMoment.unix())
          // get the line position of a block
          ?.map((block) => {
            const unavailableLines = [];
            let line = 1;
            let availableLine = 0;
            for (let i = arrayLinePositions.length - 1; i >= 0; i -= 1) {
              // If the starttime is before endtime of the array element and they or not equal
              // => start new line
              if (
                !block.startMoment.isAfter(arrayLinePositions[i].endMoment) &&
                !block.startMoment.isSame(arrayLinePositions[i].endMoment) &&
                !block.isDeleted
              ) {
                // this line is not possible
                unavailableLines.push(arrayLinePositions[i].line);
                line += 1;
              } else if (!unavailableLines.includes(arrayLinePositions[i].line)) {
                // set availableLine
                availableLine = arrayLinePositions[i].line;
                if (availableLine === 1) {
                  // no need to go further, just break
                  break;
                }
              }
            }
            if (availableLine && availableLine < line) {
              line = availableLine;
            }

            if (!block.isDeleted) {
              arrayLinePositions.push({
                ...block,
                line,
              });
            }

            return {
              ...block,
              line,
            };
          })
        : [],
    );
  }, [blocks, showTravelTime]);

  const showConfirmedToTentative = (key) => {
    if (!doNotShowReschedule) {
      notification.info({
        key,
        btn: <ConfirmButton
          type='primary'
          size='small'
          onClick={() => notification.destroy('confirmedToTentative')}
        >
          {intl.formatMessage({ id: 'common.OK' })}
        </ConfirmButton>,
        message:
          intl.formatMessage({ id: 'planningBoard.blocks.PLANNED_TO_TENTATIVE_TITLE' }),
        description: <>
          {intl.formatMessage({ id: 'planningBoard.blocks.PLANNED_TO_TENTATIVE' })}
          {checkbox}
        </>,
        duration: 0,
        placement: 'top',
        className: 'extended-notification',
      });
    }
  };

  const handleAdminUser = (droppedItem, parameters, skipPreview = false) => {
    if (droppedItem.isAssignedPlanner) {
      updateBlockData(parameters);
    } else if (droppedItem.isUserAdminForScope) {
      setBlockPreview(parameters);
      notification.warning({
        message: intl.formatMessage({ id: 'planningBoard.blocks.ADMIN_WARNING_TITLE' }),
        description: getWarningDescription(() => {
          notification.destroy('assignedPlannerWarning');
          updateBlockData({
            ...parameters,
            skipPreview,
          });
        }, intl, () => setBlockPreviewFailure(parameters)),
        className: 'extended-notification',
        key: 'assignedPlannerWarning',
        closeIcon: <CloseOutlined onClick={() => setBlockPreviewFailure(parameters)} />,
        duration: 0,
        placement: 'top',
      });
    }
  };

  const handleAdminUserMultiPlanningItems = (type, droppedInLaneId, dayHourDropped) => {
    const isNotAssignedPlanningItems =
      selectedIds?.filter(item => !item?.isAssignedPlanner);
    const isUserAdminPlanningItems =
      selectedIds?.filter(item => item?.isUserAdminForScope);

    // if there is one planning item found that is not assigend we will not send it to MW
    if (!isNotAssignedPlanningItems?.length) {
      updateMultiPlanningBlockData({
        entities: selectedIds?.map(planningItem => ({
          linkedEntityId: planningItem?.id,
          linkedEntityType: type,
          planningItemObject: planningItem,
        })),
        boardId: viewConfig.boardIdTypes.service,
        laneId: droppedInLaneId,
        start: dayHourDropped,
      });
    } else if (isUserAdminPlanningItems?.length) {
      notification.warning({
        message: intl.formatMessage({ id: 'planningBoard.blocks.ADMIN_WARNING_TITLE' }),
        description: getWarningDescription(() => {
          notification.destroy('assignedPlannerWarning');
          updateMultiPlanningBlockData({
            entities: selectedIds?.map(planningItem => ({
              linkedEntityId: planningItem?.id,
              linkedEntityType: type,
              planningItemObject: planningItem,
            })),
            boardId: viewConfig.boardIdTypes.service,
            laneId: droppedInLaneId,
            start: dayHourDropped,
          });
        }, intl),
        className: 'extended-notification',
        key: 'assignedPlannerWarning',
        duration: 0,
        placement: 'top',
      });
    }
  };

  useEffect(() => {
    // blocks are mapped
    if (mappedBlocks && mappedBlocks.length) {
      // get the highest line to set the totalLines
      const totalLines = getTotalLines(mappedBlocks);
      setTotalLines(totalLines);
      setLaneInfo({ id, totalLines });
    } else {
      setTotalLines(1);
      setLaneInfo({ id, totalLines: 1 });
    }
  }, [mappedBlocks]);

  // making the lane droppable
  const [{ isDraggingOver, type }, drop] = useDrop({
    accept: [ItemTypes.BLOCK, ItemTypes.ORDER, ItemTypes.PLANNING, ItemTypes.NON_PLANNING_BLOCK],
    // eslint-disable-next-line complexity
    drop(item, monitor) {
      const clientOffset = monitor.getClientOffset();
      if (clientOffset && clientOffset.x) {
        const droppedLeftPx = clientOffset.x - laneConfig.widthFirstColumn;
        const droppedInLaneId = id;
        const droppedItem = item.data;
        const droppedItemCurrentLaneId = droppedItem?.laneId;
        let dayHourDropped;
        if (adjacentBlock && droppedItem.type === blockConfig.types.fieldService) {
          dayHourDropped = adjacentBlock.endMoment?.format('YYYY-MM-DDTHH:mm:ss');
        } else {
          dayHourDropped = getDayHourOfDroppedBlock({
            droppedLeftPx,
            ganttDimensions,
            viewDimensions,
            dateReference,
            view,
          });
        }
        // if error
        if (dayHourDropped.error) {
          notification.warning({
            message: intl.formatMessage({ id: `planningBoard.errors.${dayHourDropped.error}` }),
            placement: 'top',
          });
        } else if (type === ItemTypes.ORDER && selectedIds && selectedIds.length > 1) {
          addMultiBlockData({
            entities: selectedIds?.map(sId => ({
              linkedEntityId: sId,
              linkedEntityType: type,
            })),
            boardId: viewConfig.boardIdTypes.service,
            laneId: droppedInLaneId,
            start: dayHourDropped,
          });
        } else if (type === ItemTypes.PLANNING && selectedIds && selectedIds.length > 1) {
          handleAdminUserMultiPlanningItems(type, droppedInLaneId, dayHourDropped);
        } else if (droppedItemCurrentLaneId === droppedInLaneId) {
          // dropped in same lane ==> update
          const droppedDate = moment(dayHourDropped).format('YYYY-MM-DD');
          const parameters = {
            type: 'updateSameLane',
            boardId: viewConfig.boardIdTypes.service,
            planningId: droppedItem.id,
            linkedEntityId: droppedItem.objectId,
            isAbsenceOrReservation: droppedItem.type !== blockConfig.types.fieldService,
            laneId: droppedInLaneId,
            start: moment(dayHourDropped).set({ second: 0 }).format('YYYY-MM-DDTHH:mm:ss'),
            description: droppedItem.line1 || '',
            successCb: droppedItem.showAs === 'active' &&
              droppedItem.type === blockConfig.types.fieldService &&
              droppedDate !== moment(droppedItem?.start).format('YYYY-MM-DD') ?
              () => {
                showConfirmedToTentative('confirmedToTentative');
              } :
              null,
          };
          handleAdminUser(droppedItem, parameters);
        } else if (droppedItem?.id) {
          const droppedDate = moment(dayHourDropped).format('YYYY-MM-DD');
          const parameters = {
            type: 'updateOtherLane',
            boardId: viewConfig.boardIdTypes.service,
            planningId: droppedItem.id,
            laneId: droppedInLaneId,
            linkedEntityId: droppedItem.objectId,
            isAbsenceOrReservation: droppedItem.type !== blockConfig.types.fieldService,
            start: moment(dayHourDropped).set({ second: 0 }).format('YYYY-MM-DDTHH:mm:ss'),
            description: droppedItem.line1 || '',
            oldLaneId: droppedItemCurrentLaneId,
            successCb: droppedItem.showAs === 'active' &&
              droppedItem.type === blockConfig.types.fieldService &&
              droppedDate !== moment(droppedItem?.start).format('YYYY-MM-DD') ?
              () => {
                showConfirmedToTentative('confirmedToTentative');
              } :
              null,
          };
          handleAdminUser(droppedItem, parameters, true);
        } else {
          addBlockData({
            ...droppedItem,
            boardId: viewConfig.boardIdTypes.service,
            laneId: droppedInLaneId,
            start: moment(dayHourDropped).set({ second: 0 }).format('YYYY-MM-DDTHH:mm:ss'),
            type: blockConfig.types.fieldService,
          });
        }
      }

      // eslint-disable-next-line no-undefined
      return undefined;
    },
    collect: monitor => ({
      isDraggingOver: monitor.isOver(),
      type: monitor.getItemType(),
    }),
  });


  useEffect(() => {
    setLaneInfoIsDraggingOver({ id, isDraggingOver });
    setIsDraggingOver(isDraggingOver);
  }, [isDraggingOver]);

  const onDayClickEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedLeftPx = e.clientX - laneConfig.widthFirstColumn;
    const dayHourDropped = getDayHourOfDroppedBlock({
      droppedLeftPx,
      ganttDimensions,
      viewDimensions,
      dateReference,
      view,
    });
    renderMenu(dayHourDropped);
  };

  return (
    <DropZoneWrapper ref={drop} onClick={onDayClickEvent} className='dropzone'>
      {mappedBlocks &&
        !isLoadingBoardData &&
        mappedBlocks?.map((block, key) => {
          if (block.isDeleted) {
            return null;
          }
          const blockAfterHours = isAfterHours(block, ganttDimensions, viewDimensions);
          if (blockAfterHours) {
            // show afterHours with highest priority first
            const currentPrioAfterHours =
              blockConfig.alert[blockAfterHours?.alert?.level]?.priority;
            const currentLeftAfterHours = blockAfterHours?.left;

            if (currentLeftAfterHours === prevLeftAfterHours) {
              if (currentPrioAfterHours > prevPrioAfterHours) {
                prevLeftAfterHours = currentLeftAfterHours;
                prevPrioAfterHours = currentPrioAfterHours;
                return <BlockAfterHours block={blockAfterHours} key={key} />;
              }
              return null;
            }

            prevLeftAfterHours = currentLeftAfterHours;
            prevPrioAfterHours = currentPrioAfterHours;
            return <BlockAfterHours block={blockAfterHours} key={key} />;
          }

          const topDimension = getTopDimension(block);
          // Use initial operationInfo from block, once multistatusinfo comes in
          // use the multistatusInfo and if needed the new id
          const blockStatusInfo = multiStatusInfo?.find(i => i.id === block.id);
          const fullBlock = {
            ...block,
            newId: blockStatusInfo?.newId,
            operationInfo:
              blockStatusInfo?.operationInfo ||
              block.operationInfo,
          };

          return <BlockWrapper
            key={key}
            block={fullBlock}
            top={topDimension}
          />;
        })}
    </DropZoneWrapper>
  );
};

LaneDropzone.propTypes = {
  id: PropTypes.string.isRequired,
  blocks: PropTypes.array.isRequired,
  setTotalLines: PropTypes.func.isRequired,
  viewDimensions: PropTypes.object.isRequired,
  ganttDimensions: PropTypes.object.isRequired,
  setIsDraggingOver: PropTypes.func.isRequired,
  dateReference: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  addBlockData: PropTypes.func.isRequired,
  updateBlockData: PropTypes.func.isRequired,
  setLaneInfoIsDraggingOver: PropTypes.func.isRequired,
  selectedIds: PropTypes.array,
  renderMenu: PropTypes.func,
  view: PropTypes.view,
  setLaneInfo: PropTypes.func.isRequired,
  isLoadingBoardData: PropTypes.bool,
  addMultiBlockData: PropTypes.func,
  updateMultiPlanningBlockData: PropTypes.func,
  multiStatusInfo: PropTypes.object,
  setBlockPreview: PropTypes.func,
  setBlockPreviewFailure: PropTypes.func,
};

export default injectIntl(LaneDropzone);
